// Local reminder notifications (P0 — zero server involved).
// Web: not supported by expo-notifications; every call is a safe no-op there.

import { Platform } from 'react-native';

import type { Reminder } from '@/types/domain';
import { formatDate } from '@/utils/format';

export async function ensureNotificationSetup(): Promise<boolean> {
  if (Platform.OS === 'web') return false;
  const Notifications = await import('expo-notifications');
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: false,
      shouldSetBadge: false,
    }),
  });
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('reminders', {
      name: 'Przypomnienia',
      importance: Notifications.AndroidImportance.HIGH,
    });
  }
  const { status } = await Notifications.requestPermissionsAsync();
  return status === 'granted';
}

/** Re-schedule everything from scratch — idempotent, called after any reminder change */
export async function syncReminderNotifications(reminders: Reminder[]): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  // Cancel only OUR reminder batch — goal nudges (goal-*), vault dates
  // (vault-*) and income prompts (income-*) live their own life; a blanket
  // cancelAll would silently kill them on every reminder edit.
  const scheduled = await Notifications.getAllScheduledNotificationsAsync();
  for (const n of scheduled) {
    const id = n.identifier;
    if (
      !id.startsWith('goal-') &&
      !id.startsWith('vault-') &&
      !id.startsWith('income-') &&
      !id.startsWith('fixedcost-')
    ) {
      await Notifications.cancelScheduledNotificationAsync(id);
    }
  }

  const now = Date.now();
  for (const reminder of reminders.filter((r) => r.enabled)) {
    for (const leadDays of reminder.leadDaysBefore) {
      const [y, m, d] = reminder.eventDate.split('-').map(Number);
      const fireAt = new Date(y, m - 1, d, 9, 0, 0); // 09:00 local, leadDays before the event
      fireAt.setDate(fireAt.getDate() - leadDays);
      if (fireAt.getTime() <= now) continue;
      await Notifications.scheduleNotificationAsync({
        content: {
          title: reminder.title,
          body: `${reminder.subtitle ? `${reminder.subtitle} — ` : ''}${
            leadDays === 1 ? 'jutro' : `za ${leadDays} dni`
          } (${formatDate(reminder.eventDate)})`,
        },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.DATE,
          date: fireAt,
          channelId: Platform.OS === 'android' ? 'reminders' : undefined,
        },
      });
    }
  }
}

// --- Goal nudges (identifiers prefixed goal-*, exempt from the sync above) ---

/**
 * Milestone celebration push — fired right after the contribution that crossed
 * a savings milestone (owner: "powiadomienie na telefon" once real money piles up).
 */
export async function notifyGoalMilestone(goalName: string, savedPln: string): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  await Notifications.scheduleNotificationAsync({
    content: {
      title: `Uzbierałeś już ${savedPln}`,
      body: `Cel „${goalName}" rośnie. Chcesz zabezpieczyć bliskich? Ubezpieczenie na życie od ok. 49 zł/mies.`,
    },
    trigger: null, // immediate
  });
}

/**
 * "30 days of silence" nudge — (re)scheduled after every contribution and on
 * goal creation, so it only ever fires when the goal really went quiet.
 */
export async function scheduleGoalIdleReminder(goalId: string, goalName: string): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  const id = `goal-idle-${goalId}`;
  await Notifications.cancelScheduledNotificationAsync(id).catch(() => undefined);
  const fireAt = new Date();
  fireAt.setDate(fireAt.getDate() + 30);
  fireAt.setHours(18, 0, 0, 0);
  await Notifications.scheduleNotificationAsync({
    identifier: id,
    content: {
      title: `Czy dołożyłeś coś do celu „${goalName}"?`,
      body: 'Regularne odkładanie — nawet 50 zł miesięcznie — buduje nawyk i przybliża cel.',
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.DATE,
      date: fireAt,
      channelId: Platform.OS === 'android' ? 'reminders' : undefined,
    },
  });
}

export async function cancelGoalIdleReminder(goalId: string): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  await Notifications.cancelScheduledNotificationAsync(`goal-idle-${goalId}`).catch(() => undefined);
}

// --- Income prompts (identifiers income-*, exempt from the sync above) ---

/**
 * Monthly "czy wpłynęła pensja z X?" prompt on the source's expected day
 * (owner's flow: the app asks, the user types the amount). Re-schedule =
 * cancel + schedule; call with expectedDay=null to just cancel.
 */
export async function scheduleIncomePrompt(
  sourceId: string,
  sourceName: string,
  expectedDay: number | null,
): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  const id = `income-${sourceId}`;
  await Notifications.cancelScheduledNotificationAsync(id).catch(() => undefined);
  if (!expectedDay) return;
  await Notifications.scheduleNotificationAsync({
    identifier: id,
    content: {
      title: `Czy wpłynęła wypłata: ${sourceName}?`,
      body: 'Dopisz kwotę w zakładce Zarobki — zajmie 5 sekund.',
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.MONTHLY,
      day: Math.min(expectedDay, 28), // 29-31 nie istnieją w każdym miesiącu
      hour: 10,
      minute: 0,
      channelId: Platform.OS === 'android' ? 'reminders' : undefined,
    },
  });
}

export async function cancelIncomePrompt(sourceId: string): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  await Notifications.cancelScheduledNotificationAsync(`income-${sourceId}`).catch(() => undefined);
}

// --- Fixed-cost prompts (identifiers fixedcost-*, exempt from the sync above) ---

/** Monthly "czynsz — zapłacone?" prompt on the cost's due day (1st when unset). */
export async function scheduleFixedCostPrompt(
  costId: string,
  name: string,
  amountPln: string,
  dueDay: number | null,
): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  const id = `fixedcost-${costId}`;
  await Notifications.cancelScheduledNotificationAsync(id).catch(() => undefined);
  await Notifications.scheduleNotificationAsync({
    identifier: id,
    content: {
      title: `${name} ${amountPln} — zapłacone?`,
      body: 'Odhacz w aplikacji, a wydatek dopisze się do budżetu.',
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.MONTHLY,
      day: Math.min(dueDay ?? 1, 28),
      hour: 9,
      minute: 30,
      channelId: Platform.OS === 'android' ? 'reminders' : undefined,
    },
  });
}

export async function cancelFixedCostPrompt(costId: string): Promise<void> {
  if (Platform.OS === 'web') return;
  const Notifications = await import('expo-notifications');
  await Notifications.cancelScheduledNotificationAsync(`fixedcost-${costId}`).catch(() => undefined);
}
